import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Tooltip, Typography } from '@mui/joy';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CircularProgress from '@mui/joy/CircularProgress';
import axios from 'axios';
import { apiURL } from '../API/constants';
import { getPaymentStatusChipStyle } from './PaymentStatusOptionList';

const UNSETTLED_STATUSES = ['unpaid', 'partially_paid', 'overpaid'];

const stripSx = {
  mt: 0.5,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 0.5,
  minHeight: 24,
};

export default function CustomerInvoicedPaymentStatusBanner({ userId, enabled }) {
  const { t } = useTranslation('ui');
  const [loading, setLoading] = useState(false);
  const [statusDetail, setStatusDetail] = useState(null);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    if (!enabled || !userId) {
      setStatusDetail(null);
      setFetchError(false);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    const fetchStatus = async () => {
      setLoading(true);
      setFetchError(false);
      try {
        const response = await axios.get(
          `${apiURL}/auth/users/invoiced-shipment-payment-status/`,
          {
            params: { user_ids: String(userId) },
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
            withCredentials: true,
            signal: controller.signal,
          }
        );
        if (cancelled) return;
        const detail = response?.data?.results?.[userId] ?? response?.data?.results?.[String(userId)] ?? null;
        setStatusDetail(detail);
      } catch (err) {
        if (cancelled || axios.isCancel(err)) return;
        console.error('Error fetching customer payment status:', err.message);
        setStatusDetail(null);
        setFetchError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchStatus();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [enabled, userId]);

  const activeStatuses = useMemo(() => {
    if (!statusDetail?.by_payment_status) return [];
    return UNSETTLED_STATUSES.filter(
      (key) => (statusDetail.by_payment_status[key] || 0) > 0
    );
  }, [statusDetail]);

  if (!enabled || !userId) return null;

  if (loading) {
    return (
      <Box sx={stripSx}>
        <CircularProgress size="sm" sx={{ '--CircularProgress-size': '14px' }} />
        <Typography level="body-xs" textColor="text.tertiary">
          {t('widgets.customerPaymentStatus.loadingShort')}
        </Typography>
      </Box>
    );
  }

  if (fetchError || !statusDetail || !statusDetail.has_unsettled_invoiced_shipments) {
    return null;
  }

  const summaryTooltip = t('widgets.customerPaymentStatus.unsettledDescription', {
    count: statusDetail.shipment_count,
  });

  return (
    <Box sx={stripSx} role="status" aria-live="polite">
      <Tooltip title={summaryTooltip} variant="soft" placement="top">
        <WarningAmberRoundedIcon
          sx={{ fontSize: 16, color: 'warning.500', flexShrink: 0 }}
          aria-hidden
        />
      </Tooltip>
      {activeStatuses.map((statusKey) => {
        const count = statusDetail.by_payment_status[statusKey];
        const chipStyle = getPaymentStatusChipStyle(statusKey);
        return (
          <Tooltip
            key={statusKey}
            title={t(`paymentStatus.${statusKey}`)}
            variant="soft"
            placement="top"
          >
            <Chip
              size="sm"
              variant="soft"
              sx={{
                '--Chip-minHeight': '22px',
                '--Chip-paddingInline': '8px',
                fontSize: '0.75rem',
                fontWeight: 600,
                bgcolor: chipStyle.bgcolor,
                color: chipStyle.color,
              }}
            >
              {t(`paymentStatus.${statusKey}`)} · {count}
            </Chip>
          </Tooltip>
        );
      })}
    </Box>
  );
}
